import React from 'react';
import { Play } from 'lucide-react';
import '../App.css';

// The branded title card shown in a help media slot: "coming soon" for a video with no
// recording or a screenshot not yet captured, and the Play button for a video that has one.
//
// It is the opening card from the help video kit, rebuilt in CSS rather than
// screenshotted: it stays crisp at any width, needs no image asset per slot, and
// takes its wording from the block and article that are already on the page — so a
// new slot is captioned correctly without anyone exporting a card for it.
//
// With no `src` it says "coming soon". The moment one is set, a screenshot shows the
// real image instead, and a video keeps this card as its Play button (`onPlay`, via
// HelpVideo) until the reader presses it, which matches the card the video opens on.
export type MediaKind = 'video' | 'shot';

export interface MediaPlaceholderProps {
  /** Decides the wording and the aspect ratio. */
  kind?: MediaKind;
  /** The article's topic — "Relationships & Connections", "Tutorial". */
  eyebrow?: string;
  /** What this slot covers. Falls back to the article title. */
  title?: string;
  /** Planned runtime, when a video block carries one. */
  duration?: string;
  /**
   * The video exists: the card becomes its Play button instead of saying "coming soon"
   * (#1077). HelpVideo swaps in the player when it is pressed, so nothing loads from
   * YouTube until the reader asks for it.
   */
  onPlay?: () => void;
}

const MediaPlaceholder: React.FC<MediaPlaceholderProps> = ({
  kind = 'video', eyebrow, title, duration, onPlay,
}) => {
  const isShot = kind === 'shot';
  const noun = isShot ? 'Screenshot' : 'Video';
  // Screenshots keep the 16:10 the old grey box used, so swapping this in does
  // not change the height of any article that already sits on a screenshot.
  const className = `vph${isShot ? ' vph--shot' : ''}${onPlay ? ' vph--play' : ''}`;
  // A <button> may hold only phrasing content, so as the Play button the card is built
  // from spans (the CSS lays them out the same). The button's label names the video,
  // so the title is not a page heading there.
  const Box = onPlay ? 'span' : 'div';
  const Title = onPlay ? 'span' : 'h3';
  const body = (
    <>
      <Box className="vph-lockup">
        {/* The dark-background mark: the same artwork the video kit embeds. */}
        <img className="vph-glyph" src="/chaptery-mark-dark.svg" alt="" aria-hidden="true" />
        <span className="vph-wm">Chaptery</span>
        <span className="vph-tag">Help Center</span>
      </Box>

      <Box className="vph-mid">
        {eyebrow && (
          <Box className="vph-eyebrow"><span className="vph-dot" />{eyebrow}</Box>
        )}
        {title && <Title className="vph-title">{title}</Title>}
      </Box>

      {onPlay && (
        <span className="vph-playbtn" aria-hidden="true">
          <Play size={16} fill="currentColor" strokeWidth={0} />
        </span>
      )}

      <Box className="vph-foot">
        {onPlay
          ? <span className="vph-play">Play video</span>
          : <span className="vph-soon">{noun} coming soon…</span>}
        {duration && <span className="vph-dur">{duration}</span>}
      </Box>
    </>
  );

  if (onPlay) {
    return (
      <button type="button" className={className} onClick={onPlay}
        aria-label={`Play video: ${title || 'help video'}`}>
        {body}
      </button>
    );
  }
  return (
    <div className={className} role="img"
      aria-label={`${title || `Help ${noun.toLowerCase()}`} — ${noun.toLowerCase()} coming soon`}>
      {body}
    </div>
  );
};

export default MediaPlaceholder;
